<script setup lang="ts">
import { Media, ButtonsList } from '@/types/api';

interface Item {
  id: number;
  title: string;
  image?: Media,
}

const props = defineProps<{
  title: string;
  benefit_cards: Item[];
  buttons_list: ButtonsList[];
}>();
</script>

<template>
  <div class="benefit section-offset" dir="auto">
    <div class="container">
      <div class="benefit__content">
        <h3>
          {{ props.title }}
        </h3>
        <div class="benefit__scroll">
          <div class="benefit__wrapper">
            <div v-for="item in props.benefit_cards" :key="item.title" class="benefit__item">
              <img v-if="item.image" :src="item.image.url" alt="" loading="lazy">
              <div class="benefit__item-bottom">
                <p>{{ item.title }}</p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="props.buttons_list" class="benefit__buttons">
          <a
            v-for="(button, index) in props.buttons_list" :key="index" :href="button?.button?.href"
            :target="button?.button?.target" class="btn dc-link-button"
          >
            {{ button?.button?.title }}
            <img v-if="button?.button?.icon" :src="button?.button?.icon?.url">
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/mixins.scss';
@import '@/assets/scss/variables.scss';

.title--start {
  text-align: start;

  @media (max-width: 1087px) {
    text-align: center;
  }
}

.benefit {
  &__content {
    h3 {
      color: #2d2d36;
      font-size: 36px;
      font-weight: 600;
      line-height: 115%;
      margin-bottom: 16px;

      @include xl {
        margin-bottom: 0;
      }

      @media (max-width: 767px) {
        font-size: 24px;
        line-height: 120%;
        margin-bottom: 20px;
        text-align: start;
      }
    }
  }

  &__scroll {
    overflow-y: hidden;
    overflow-x: auto;

    padding-bottom: 20px;
    margin-bottom: -20px;
    margin-left: -32px;
    margin-right: -16px;

    padding-left: 32px;
    padding-right: 16px;


    @include md {
      margin-left: -16px;
      margin-right: -16px;

      padding-left: 16px;
      padding-right: 16px;
    }
  }

  &__wrapper {
    position: relative;
    display: -ms-grid;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    -ms-grid-gap: 16px;
    grid-gap: 16px;
    padding-top: 40px;
    min-width: 870px;

    @include md {
      padding-top: 20px;
      min-width: fit-content;
    }
  }

  .dc-link-button {
    @media (min-width: 768px) {
      max-width: 270px;
      margin: 0 auto;
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: flex-end;
    padding: 24px;
    background: $surface-ds-surface-primary;
    border-radius: 28px;
    position: relative;
    width: auto;
    padding-top: 80%;

    @include xl {
      min-width: 240px;
      max-width: 288px;
    }

    @include md {
      width: 240px;
      padding-top: 67%;
    }

    img {
      display: block;
      position: absolute;
      max-width: 90%;
      inset-inline-start: 0;
      width: 200px;
      top: -40px;

      @include xl {
        width: 170px;
        top: -20px;
      }
    }

    p {
      color: #141414;
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: 28px;
      text-align: start;
    }
  }

  &__buttons {
    margin-top: var(--spacing-7, 24px);

    text-align: start;
    display: flex;
    gap: 16px;
    justify-content: flex-start;
  }

  & .btn {
    margin: 0;
    border-radius: 12px;
    border: 1px solid #DEDEED;
    background: #FFF;
    height: 56px;
    width: 100%;
    padding: 16px 24px;
    color: #2D2D36;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    img {
      margin-inline-start: 8px;
    }

    &:hover {
      color: #5A2CF2;
      border-radius: 12px;
      border: 1px solid #5A2CF2 !important;
      background: #F0F0F7;

      img {
        filter: brightness(0) saturate(100%) invert(22%) sepia(89%) saturate(7452%) hue-rotate(256deg) brightness(97%) contrast(95%);
      }

      &::after {
        opacity: 1;
      }
    }
  }
}
</style>
